<script>
  import {
    bindIframeAutoResize,
    buildNoteIframeSrcdoc,
  } from '@/components/search/email/emailBodyHtml';
  import { escapeHtmlText, noteBodyIsHtmlDocument } from '@/components/search/email/emailConnectCore';

  export default {
    name: "NoteExpansion",
    props: {
      colspan: {
        required: true
      },
      files: {
        default: []
      },
      html: {
        type: Boolean,
        default: false,
      },
      nid: {
        required: true
      },
      note: {},
      tasks: {},
      type: {},
      width: '',// 13 Jun 2025 JCH (merged in rebase) - updates width handling
    },
    data() {
      return {
        iframeRevealed: false,
        iframeHeightPx: null,
      };
    },
    watch: {
      nid: 'resetIframeLayout',
      note: 'resetIframeLayout',
      html: 'resetIframeLayout',
      type: 'resetIframeLayout',
    },
    methods: {
      resetIframeLayout() {
        this.iframeRevealed = false;
        this.iframeHeightPx = null;
      },
    },
    render(createElement) {
      const normalizedType = String(this.type || '').toLowerCase().trim();
      const webNote = normalizedType === 'web' || normalizedType === 'web in' || normalizedType === 'webin';
      // 18 Sep 2026 JCH: NC-1641 — plain email in went through raw innerHTML below, so <addresses> in the stamp were eaten
      // (the same bug the open note fixed). Every email in now takes the same iframe path as EditNoteEmail; the html flag
      // decides HTML vs escaped plain text.
      const emailNote = normalizedType === 'email in';
      const iframeNote = webNote || emailNote;
      let children = [];

      if (this.tasks) {
        let task = createElement('div', {}, [
          createElement('p', {
            attrs: {
              class: 'font-italic',
              width: this.width,// 13 Jun 2025 JCH (merged in rebase) - updates width handling
            },
          }, 'Comments: '),
          createElement('p', {
            domProps:{
              innerHTML:`<div style="color: grey; width: ${this.width} !important; background-color: transparent !important">`
                + this.tasks.replace(/(?:\r\n|\r|\n)/g, '<br>').replace(/\uFFFD/g, "'") + '</div><br>'
            }
          })
        ]);
        children.push(task);
      }

      if (this.note) {
        let note;
        if (iframeNote) {
          // web notes: same srcdoc builder as the open note (guess + pad). Raw srcdoc left a
          // text-only body with no lastElementChild, so height measure fell back to the viewport.
          const srcdoc = emailNote
            ? buildNoteIframeSrcdoc(this.note, { treatAsHtml: !!this.html })
            : buildNoteIframeSrcdoc(this.note, { padDocument: true });
          const cap = Math.round(window.innerHeight * 0.55);
          const revealed = this.iframeRevealed;
          note = createElement('div', {
            style: { position: 'relative', width: this.width || '100%' },
          }, [createElement('iframe', {
            attrs: {
              srcdoc,
              sandbox: 'allow-same-origin allow-popups allow-popups-to-escape-sandbox',
              title: 'Formatted note body',
            },
            style: {
              width: '100%',
              // first paint: hidden + out of flow at the cap so we can measure wrapping. After reveal,
              // keep those styles — a later files-load re-render used to force hidden again and the
              // call log vanished (load does not re-fire when srcdoc is unchanged).
              height: `${this.iframeHeightPx || cap}px`,
              visibility: revealed ? 'visible' : 'hidden',
              position: revealed ? '' : 'absolute',
              left: revealed ? '' : '0',
              top: revealed ? '' : '0',
              border: '0',
              display: 'block',
            },
            on: {
              // 18 Sep 2026 JCH: NC-1641 — fit the box to the note like the open note does (same helper). Cap at 55% of
              // the window so the whole box stays inside the visible list with the row header/collapse still on screen;
              // only notes longer than that scroll inside. Floor keeps two-line notes from sitting in a big empty box.
              load: (ev) => {
                const iframe = ev.target;
                if (iframe._nuIframeResizeObserver) iframe._nuIframeResizeObserver.disconnect(); // reload: drop the old doc's observer (as EditNoteEmail does)
                bindIframeAutoResize(iframe, {
                  minHeight: 80,
                  maxHeight: cap,
                  reveal: true,
                  onReveal: () => {
                    this.iframeRevealed = true;
                    if (iframe._nuLastAutoHeight) this.iframeHeightPx = iframe._nuLastAutoHeight;
                  },
                });
              },
            },
          })]);
        } else {
          // 18 Sep 2026 JCH: NC-1641 — line breaks are added here now (FirmNotesTable no longer pre-inserts <br>). The
          // html flag decides: a plain note (html off) is escaped so a Copy of an email keeps its <addresses> — and text
          // like "plan <A>" — instead of the browser eating them. Matches the open note, which shows an html-off body
          // literally in a textarea. (Sniffing the text was fooled by a lone <A>/<addr@x.com> that looks like a tag.)
          const raw = String(this.note);
          const authoredHtml = normalizedType === 'status' || normalizedType === 'news';
          // Status / news are old authored HTML without the html flag. A connected-email stamp
          // (email-note-header / full document) is the same test Copy uses — don't escape those.
          // Memos with a lone <A> still escape.
          let body;
          if (this.html) body = raw;
          else if (authoredHtml || noteBodyIsHtmlDocument(raw)) body = raw.replace(/(?:\r\n|\r|\n)/g, '<br>');
          else body = escapeHtmlText(raw).replace(/(?:\r\n|\r|\n)/g, '<br>');
          note = createElement('div', {
            attrs: {
              class: 'note'
            },
            domProps:{
              innerHTML:`<div style="width: ${this.width} !important; background-color: transparent !important">` + body.replace(/\uFFFD/g, "'") + '</div>' // 13 Jun 2025 JCH (merged in rebase) - updates width handling
            }
          });
        }

        children.push(note);
      }

      if (this.files.length) {
        let fileLinks = [];
        fileLinks.push(createElement('br'));// need space here
        this.files.forEach(file => {
            let link = createElement('a', {
              attrs: {
                href: `${process.env.VUE_APP_UW_URL}/download/${file.loidfid}`,
                width: this.width,// 13 Jun 2025 JCH (merged in rebase) - updates width handling
              },
              style: {
                color: 'blue'
              }
            }, `${file.file_description}`);
            fileLinks.push(link, createElement('br'));
        });

        let files = createElement('div', {}, fileLinks);
        children.push(files)
      }

      return createElement('td', {
        attrs: {
          style: 'background-color: #f9f9f9',// 13 Jun 2025 JCH (merged in rebase) slightly lighter than whitesmoke to make expansions easier to spot
          class: 'py-6',
          colspan: this.colspan,// 18 Feb 2026 JCH - use provided table colspan; webColspan is undefined and causes width/layout issues
        }
      }, children);
    },
  }
</script>

<style scoped>
    .note table {
        border-collapse: collapse;
        margin: 0 auto;
        width: auto !important;
    }
    .note table, .note th, .note td, .note tr {
        border: 1px solid black !important;
    }
    .note td {
        padding: 1em;
    }
</style>
